import React from 'react';
import { motion } from 'framer-motion';
import HeaderVariant from '../components/common/HeaderVariant';
import Footer from '../components/Footer/Footer';

const AboutUsPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <HeaderVariant />
      
      <main className="container mx-auto px-4 py-24">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-5xl md:text-6xl font-bold text-center mb-20 pt-24"
        >
          About Jacks
        </motion.h1>

        <div className="grid md:grid-cols-2 gap-16 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-4xl font-bold mb-6">Welcome to Jacks</h2>
            <p className="text-xl mb-6">Revolutionizing the Personal Driver Experience.</p>
            <p className="text-gray-300 mb-6">
              Jacks is changing the way people think about personal transportation. With our 
              on-demand marketplace app, you can hire professional drivers to drive you in 
              the comfort of your own car – no long-term commitments, no hassle, and 
              entirely on your schedule.
            </p>
            <p className="text-gray-300">
              We are committed to creating a service that puts you first. Everything we do is 
              designed to simplify your life, elevate your experience, and empower the 
              Jacks community of drivers and clients alike.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-4xl font-bold mb-6">What We Do</h2>
            <p className="text-xl italic mb-6">A modern solution</p>
            <p className="text-gray-300 mb-6">
            Jacks provides a modern solution to personal transportation. Whether it's for daily errands, a special event, 
              or simply getting home safely, our app connects you with vetted, professional 
              drivers who come to you. They take the wheel of your car so you can focus on 
              what matters most.
            </p>
            <p className="text-gray-300">
              Forget about hiring full-time drivers or spending on costly chauffeur services. 
              With Jacks, you get the flexibility of booking a driver when you need one, 
              without any long-term obligations.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-24"
        >
          <h2 className="text-4xl font-bold mb-12 text-center">How It Works</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4">1. Book a Driver on Demand</h3>
              <p className="text-gray-300">
                Open the Jacks app, set your pickup location, time, and duration, and request 
                a driver instantly.
              </p>
            </div>
            <div className="bg-gray-900 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4">2. Professional Drivers Come to You</h3>
              <p className="text-gray-300">
                A qualified driver arrives at your location, ready to drive your car wherever 
                you need to go.
              </p>
            </div>
            <div className="bg-gray-900 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4">3. Enjoy a Seamless Experience</h3>
              <p className="text-gray-300">
                Relax while your driver takes care of the journey, offering a safe, professional, 
                and convenient ride.
              </p>
            </div>
            <div className="bg-gray-900 p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4">4. Transparent Pricing and Secure Payments</h3>
              <p className="text-gray-300">
                Payments are processed directly through the app, with clear, upfront pricing. 
                No hidden fees or surprises.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-24"
        >
          <h2 className="text-4xl font-bold mb-12">Why Jacks is Different</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">On-Demand Flexibility</h3>
              <p className="text-gray-300 mb-8">
                No commitments, no contracts – book a driver when you need one, for as little 
                as 3 hours.
              </p>
              
              <h3 className="text-2xl font-bold mb-4">Safe and Reliable</h3>
              <p className="text-gray-300 mb-8">
                All Jacks drivers are carefully vetted to ensure your safety 
                and satisfaction.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">Convenient and Affordable</h3>
              <p className="text-gray-300 mb-8">
                Experience the luxury of a personal driver at a fraction of the cost of 
                traditional services.
              </p>
              
              <h3 className="text-2xl font-bold mb-4">Community-Driven</h3>
              <p className="text-gray-300">
                Jacks empowers drivers to earn on their terms while delivering exceptional 
                service to clients.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="text-center"
        >
          <h2 className="text-4xl font-bold mb-8">Everything We Do is for You</h2>
          <p className="text-gray-300 max-w-3xl mx-auto mb-12">
            At Jacks, our clients are at the heart of everything we do. We're here to 
            simplify your life and provide a premium experience with every ride. By 
            empowering our drivers and fostering a strong community, we're creating a 
            transportation service that benefits everyone.
          </p>
          
          <div className="bg-gray-900 rounded-lg p-12 max-w-3xl mx-auto">
            <p className="text-2xl font-semibold mb-8">
              Ready for a new way to ride?
            </p>
            <p className="text-xl mb-12">
              Download Jacks today and experience the future of personal driving.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <motion.a 
                href="https://apps.apple.com/us/app/book-jacks/id6738385560" 
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                className="block"
              >
                <img
                  src="/assets/icons/appstore-btn.png"
                  alt="Download on App Store"
                  className="h-14 cursor-pointer hover:opacity-80 transition-opacity"
                />
              </motion.a>
              <motion.a 
                href="#" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                className="block"
              >
                <img
                  src="/assets/icons/playstore-btn.png"
                  alt="Get it on Google Play"
                  className="h-14 cursor-pointer hover:opacity-80 transition-opacity"
                />
              </motion.a>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUsPage; 